// Client-side boulders: turns the server's one-off "rocks" message into instanced meshes.
//
// Placement comes from the server (rocks.py), the same placements MuJoCo uses for contact,
// so what you see is what the quadruped bumps into. Sim (x, y, z) maps to three (x, z, -y),
// a proper rotation, so a sim yaw about +z is the same angle about three's +y and triangle
// winding is preserved. Render assets live in rockAssets.ts (CC0, Poly Haven).
import * as THREE from 'three';
import { ROCK_MESHES, ROCK_TEXTURES } from './rockAssets';
import type { RockMeshAsset } from './rockAssets';

export interface RocksMessage {
  type: 'rocks';
  types: string[];                  // e.g. ['a', 'b']
  items: number[][];                // [type index, x, y, z, yaw, scale] in sim coordinates
}

function b64(s: string): ArrayBuffer {
  const bin = atob(s);
  const u = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u[i] = bin.charCodeAt(i);
  return u.buffer;
}

function buildGeometry(a: RockMeshAsset): THREE.BufferGeometry {
  const q = new Uint16Array(b64(a.pos));
  const nq = new Int8Array(b64(a.nor));
  const uq = new Uint16Array(b64(a.uv));
  const idx = new Uint16Array(b64(a.idx));
  const n = a.n;
  const pos = new Float32Array(n * 3), nor = new Float32Array(n * 3), uv = new Float32Array(n * 2);
  const sx = (a.hi[0] - a.lo[0]) / 65535, sy = (a.hi[1] - a.lo[1]) / 65535, sz = (a.hi[2] - a.lo[2]) / 65535;
  for (let i = 0; i < n; i++) {
    // sim (x, y, z) -> three (x, z, -y)
    const x = a.lo[0] + q[i * 3] * sx, y = a.lo[1] + q[i * 3 + 1] * sy, z = a.lo[2] + q[i * 3 + 2] * sz;
    pos[i * 3] = x; pos[i * 3 + 1] = z; pos[i * 3 + 2] = -y;
    let nx = nq[i * 3] / 127, ny = nq[i * 3 + 1] / 127, nz = nq[i * 3 + 2] / 127;
    const l = Math.hypot(nx, ny, nz) || 1; nx /= l; ny /= l; nz /= l;
    nor[i * 3] = nx; nor[i * 3 + 1] = nz; nor[i * 3 + 2] = -ny;
    uv[i * 2] = uq[i * 2] / 65535; uv[i * 2 + 1] = uq[i * 2 + 1] / 65535;
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
  g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  g.setIndex(new THREE.BufferAttribute(idx, 1));
  g.computeBoundingSphere();
  return g;
}

function hash1(i: number): number {
  let h = Math.imul(i + 1, 0x9e3779b1) >>> 0;
  h ^= h >>> 15; h = Math.imul(h, 0x85ebca6b) >>> 0; h ^= h >>> 13;
  return (h >>> 0) / 4294967296;
}

export class RockView {
  private readonly scene: THREE.Scene;
  private meshes: THREE.InstancedMesh[] = [];
  private material: THREE.MeshStandardMaterial | null = null;
  private textures: THREE.Texture[] = [];
  private geoms: THREE.BufferGeometry[] = [];

  constructor(scene: THREE.Scene) { this.scene = scene; }

  get count(): number { return this.meshes.reduce((n, m) => n + m.count, 0); }

  load(msg: RocksMessage) {
    this.dispose();
    const loader = new THREE.TextureLoader();
    const tex = (src: string, srgb: boolean) => {
      const t = loader.load(src);
      t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
      t.anisotropy = 4;
      this.textures.push(t);
      return t;
    };
    const mat = new THREE.MeshStandardMaterial({
      map: tex(ROCK_TEXTURES.diff, true),
      normalMap: tex(ROCK_TEXTURES.nor, false),
      roughnessMap: tex(ROCK_TEXTURES.rough, false),     // read from the green channel
      roughness: 1.0, metalness: 0.0,
    });
    this.material = mat;

    const tint = new THREE.Color();
    msg.types.forEach((name, ti) => {
      const asset = ROCK_MESHES[name];
      const rows = msg.items.filter(r => r[0] === ti);
      if (!asset || rows.length === 0) return;
      const geom = buildGeometry(asset);
      this.geoms.push(geom);
      const im = new THREE.InstancedMesh(geom, mat, rows.length);
      const m = new THREE.Matrix4(), p = new THREE.Vector3(), qt = new THREE.Quaternion(), s = new THREE.Vector3();
      const up = new THREE.Vector3(0, 1, 0);
      rows.forEach((r, k) => {
        const [, x, y, z, yaw, scale] = r;
        p.set(x, z, -y);
        qt.setFromAxisAngle(up, yaw);
        s.setScalar(scale);
        m.compose(p, qt, s);
        im.setMatrixAt(k, m);
        // a little per-rock variation so the same two boulders do not read as copies
        const v = hash1(Math.round(x * 131 + y * 71) + ti);
        const l = 0.82 + 0.3 * v;
        tint.setRGB(l * (1.0 + 0.04 * (v - 0.5)), l, l * (1.0 - 0.06 * (v - 0.5)));
        im.setColorAt(k, tint);
      });
      im.instanceMatrix.needsUpdate = true;
      if (im.instanceColor) im.instanceColor.needsUpdate = true;
      im.castShadow = true;
      im.receiveShadow = true;
      im.frustumCulled = false;       // instances are spread over the whole world
      this.scene.add(im);
      this.meshes.push(im);
    });
  }

  /** Show or hide all boulders (debug toggle). */
  setEnabled(e: boolean) { for (const m of this.meshes) m.visible = e; }

  dispose() {
    for (const m of this.meshes) { this.scene.remove(m); m.dispose(); }
    this.meshes = [];
    for (const g of this.geoms) g.dispose();
    this.geoms = [];
    for (const t of this.textures) t.dispose();
    this.textures = [];
    if (this.material) { this.material.dispose(); this.material = null; }
  }
}
